// src/services/dicomService.js

const API_BASE_URL = process.env.REACT_APP_API_ADDRESS;

// Helper to decode Base64 JSON headers (Used in de-pseudonymization)
const decodeBase64Utf8Json = (b64) => {
  if (!b64) return null;
  try {
    const binary = atob(b64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const jsonText = new TextDecoder('utf-8').decode(bytes);
    return JSON.parse(jsonText);
  } catch {
    return null;
  }
};

// -----------------------------
// LOAD & PREVIEW DICOM
// -----------------------------
export const loadDicomPreview = async (file, previewMode = 'first_frame') => {
  try {
    // Note: Adjust URL if your Django urls.py mapping is slightly different
    const url = new URL(`${API_BASE_URL}/anonymization/pii/dicom/load/`);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('preview_mode', previewMode); // 'first_frame' or 'video'

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: formData,
    });

    let body = null;
    try {
      body = await response.json();
    } catch {
      body = { detail: 'Failed to parse server response.' };
    }

    if (!response.ok) {
      return {
        success: false,
        data: null,
        statusCode: response.status,
        error: body.detail || body,
      };
    }

    // Expected shape: { filename, frames_count, metadata, preview: { type, data } }
    return {
      success: true,
      data: body,
      statusCode: response.status,
      error: null,
    };
  } catch (error) {
    console.error('Error loading DICOM preview', error);
    return {
      success: false,
      data: null,
      statusCode: 500,
      error: error.message,
    };
  }
};

// -----------------------------
// DICOM PSEUDO-ANONYMIZATION
// -----------------------------
export const pseudoAnonymizeDicomPII = async (file, options = {}) => {
  try {
    const url = new URL(`${API_BASE_URL}/anonymization/pii/dicom/pseudoanonymize/`);

    const formData = new FormData();
    formData.append('file', file);

    const safeOptions = options && typeof options === 'object' ? options : {};

    const {
      profileId,
      defaultTemplateId,
      anonymizationGroupId,
      ...restOptions
    } = safeOptions;

    if (anonymizationGroupId) {
      formData.append('anonymization_group_id', String(anonymizationGroupId));
    }

    if (defaultTemplateId) {
      formData.append('default_anonymization_profile_template_id', String(defaultTemplateId));
    } else if (profileId) {
      formData.append('anonymization_profile_id', String(profileId));
    } else if (Object.keys(restOptions).length) {
      formData.append('options', JSON.stringify(restOptions));
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: formData,
    });

    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch {
        errorBody = { detail: 'Failed to pseudo-anonymize DICOM.' };
      }
      return {
        success: false,
        data: null,
        statusCode: response.status,
        error: errorBody.detail || errorBody,
      };
    }

    const blob = await response.blob();

    const contentDisposition = response.headers.get('Content-Disposition') || '';
    let filename = 'pseudonymized_file.dcm';
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = match[1];
    }

    const vaultId = response.headers.get('X-Pseudonym-Vault-Id');

    return {
      success: true,
      data: { blob, filename, vaultId },
      statusCode: response.status,
      error: null,
    };
  } catch (error) {
    console.error('Error pseudo-anonymizing DICOM PII', error);
    return {
      success: false,
      data: null,
      statusCode: 500,
      error: error.message,
    };
  }
};

// -----------------------------
// DICOM DE-PSEUDO-ANONYMIZATION
// -----------------------------
export const dePseudoAnonymizeDicomPII = async (file) => {
  try {
    const url = new URL(`${API_BASE_URL}/anonymization/pii/dicom/depseudoanonymize/`);

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: formData,
    });

    if (!response.ok) {
      let errorBody = null;
      try { errorBody = await response.json(); }
      catch { errorBody = { detail: 'Failed to de-pseudonymize DICOM.' }; }
      return { success: false, data: null, statusCode: response.status, error: errorBody.detail || errorBody };
    }

    // Read integrity stats headers
    const statsB64 = response.headers.get('X-Depseudo-Stats');
    const depseudoStats = decodeBase64Utf8Json(statsB64);

    const replacedCount = Number(response.headers.get('X-Depseudo-Replaced-Count') || 0);
    const mappingTotal = Number(response.headers.get('X-Depseudo-Mapping-Total') || 0);

    const blob = await response.blob();

    const cd = response.headers.get('Content-Disposition') || '';
    let filename = 'depseudonymized.dcm';
    const match = cd.match(/filename="?([^"]+)"?/);
    if (match && match[1]) filename = match[1];

    return {
      success: true,
      data: {
        blob,
        filename,
        stats: depseudoStats,
        replacedCount,
        mappingTotal,
      },
      statusCode: response.status,
      error: null,
    };
  } catch (e) {
    return { success: false, data: null, statusCode: 500, error: e.message };
  }
};